import { forwardRef, TextareaHTMLAttributes, useId } from "react";
import type { FieldError } from "react-hook-form";
import { cn } from "@/utils/helpers";
import { KitFieldMessage, kitLabelClass } from "./Field";

export interface KitTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: FieldError | string;
  /** A faint line under the field. Hidden while an error shows. */
  hint?: string;
  wrapperClassName?: string;
}

/*
 * Input · Multiline (CreativeOS Library.lib.pen, style B): the Input for more
 * than one line. The same label, control glass and radius 12, words at 14 on
 * 1.45, a 2px accent ring on focus and the danger ring on error.
 */
export const KitTextarea = forwardRef<HTMLTextAreaElement, KitTextareaProps>(
  ({ label, error, hint, wrapperClassName, className, id, rows = 3, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : error?.message;

    return (
      <div className={cn("flex flex-col gap-2", wrapperClassName)}>
        {label && (
          <label htmlFor={inputId} className={kitLabelClass}>
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          aria-invalid={hasError || undefined}
          {...props}
          className={cn(
            "co-input-own-focus co-glass-control caret-co-accent w-full resize-none rounded-card px-3.5 py-3 text-[14px] leading-[1.45] text-co-text placeholder:text-co-faint",
            "outline-offset-0 focus:outline-2 focus:outline-co-accent forced-colors:focus:outline-[Highlight]",
            hasError && "outline-2 outline-co-danger focus:outline-co-danger",
            className,
          )}
        />
        {hasError && message ? (
          <KitFieldMessage>{message}</KitFieldMessage>
        ) : hint ? (
          <span className="text-[12.5px] leading-[1.45] text-co-faint">{hint}</span>
        ) : null}
      </div>
    );
  },
);

KitTextarea.displayName = "KitTextarea";
